import { Bars3Icon, PlusIcon } from "@heroicons/react/24/outline";
import { PipelineId, RegionalScope } from "@sp-crm/core";
import { QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    PipelineStageSummary,
    PipelineSummary,
} from "components/referral-pipelines/types";
import { trpc } from "components/rpc/trpc";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { fancyAlert } from "components/ui/fancy-alert";
import { Input } from "components/ui/input";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Formik, FormikHelpers } from "formik";
import { produce } from "immer";
import React, { useCallback } from "react";
import {
    calculateNewOrder,
    useReorderableListDrag,
    useReorderableListDrop,
} from "util/dnd";
import { handleEvent } from "util/user-events";

interface StageNameFormValues {
    name: string;
}

interface ReferralPipelineStageEditorProps {
    pipeline: PipelineSummary;
    scope: RegionalScope;
    onChanged: () => void;
}

export const ReferralPipelineStageEditor: React.FC<
    ReferralPipelineStageEditorProps
> = props => {
    const { pipeline, scope, onChanged } = props;
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const scopeRegionId = scope.kind === "office" ? scope.regionId : null;
    const listQueryKey: QueryKey = React.useMemo(
        () =>
            scopeRegionId
                ? trpcClient.pipeline.list.queryOptions({ regionId: scopeRegionId })
                      .queryKey
                : trpcClient.pipeline.agencyList.queryOptions().queryKey,
        [trpcClient, scopeRegionId],
    );
    const [isAdding, setIsAdding] = React.useState(false);
    const startAdding = useCallback(() => setIsAdding(true), []);
    const stopAdding = useCallback(() => setIsAdding(false), []);
    const setStageOrdersMutation = useMutation({
        ...trpcClient.pipeline.setStageOrders.mutationOptions(),
    });

    const handleReorder = useCallback(
        (draggingIndex: number, targetIndex: number) => {
            queryClient.setQueryData(
                listQueryKey,
                (oldData: PipelineSummary[] | undefined) => {
                    if (!oldData) {
                        return oldData;
                    }
                    return produce(oldData, draft => {
                        const draftPipeline = draft.find(p => p.id === pipeline.id);
                        if (!draftPipeline) {
                            return;
                        }
                        const newOrder = calculateNewOrder(
                            draftPipeline.stages,
                            draggingIndex,
                            targetIndex,
                        );
                        const current = draftPipeline.stages[draggingIndex];
                        const actual = draftPipeline.stages.find(
                            s => s.id === current.id,
                        );
                        if (!actual) {
                            return;
                        }
                        actual.order = newOrder;
                        draftPipeline.stages.sort((a, b) => a.order - b.order);
                    });
                },
            );
        },
        [queryClient, listQueryKey, pipeline.id],
    );

    const handleCommitOrder = useCallback(async () => {
        const pipelines = queryClient.getQueryData<PipelineSummary[]>(listQueryKey);
        const currentPipeline = pipelines?.find(p => p.id === pipeline.id);
        if (!currentPipeline || currentPipeline.stages.length === 0) {
            return;
        }
        await setStageOrdersMutation.mutateAsync({
            orders: currentPipeline.stages.map((stage, index) => ({
                stageId: stage.id,
                order: index,
            })),
        });
        handleEvent("referral-pipeline-stage-reorder", {
            stageCount: currentPipeline.stages.length,
        });
        onChanged();
    }, [queryClient, listQueryKey, pipeline.id, setStageOrdersMutation, onChanged]);

    const handleRevertOrder = useCallback(() => {
        onChanged();
    }, [onChanged]);

    return (
        <ul className="divide-y divide-gray-200">
            {pipeline.stages.map((stage, index) => (
                <ReferralPipelineStageRow
                    key={stage.id}
                    pipelineId={pipeline.id}
                    stage={stage}
                    index={index}
                    commitOrder={handleCommitOrder}
                    revertOrder={handleRevertOrder}
                    reorder={handleReorder}
                    onChanged={onChanged}
                />
            ))}
            <li className="py-2">
                {isAdding ? (
                    <ReferralPipelineStageRowNew
                        pipelineId={pipeline.id}
                        close={stopAdding}
                        onChanged={onChanged}
                    />
                ) : (
                    <button
                        type="button"
                        onClick={startAdding}
                        className={defaultLinkStyle}>
                        <div className="flex items-center space-x-1">
                            <PlusIcon className="w-5 h-5" />
                            <span className="text-sm">Add stage</span>
                        </div>
                    </button>
                )}
            </li>
        </ul>
    );
};

interface ReferralPipelineStageRowProps {
    pipelineId: PipelineId;
    stage: PipelineStageSummary;
    index: number;
    commitOrder: () => void;
    revertOrder: () => void;
    reorder: (draggingIndex: number, targetIndex: number) => void;
    onChanged: () => void;
}

const ReferralPipelineStageRow: React.FC<ReferralPipelineStageRowProps> = props => {
    const { pipelineId, stage, index, commitOrder, revertOrder, reorder, onChanged } =
        props;
    const trpcClient = trpc.useTRPC();
    const [isEditing, setIsEditing] = React.useState(false);
    const renameMutation = useMutation({
        ...trpcClient.pipeline.renameStage.mutationOptions(),
    });
    const deleteMutation = useMutation({
        ...trpcClient.pipeline.deleteStage.mutationOptions(),
    });

    const ref = React.useRef<HTMLLIElement>(null);
    const type = `referral-pipeline-stages-${pipelineId}`;
    const [{ isDragging }, drag, preview] = useReorderableListDrag({
        type,
        index,
        commitOrder,
        revertOrder,
        id: stage.id,
    });
    const [, drop] = useReorderableListDrop({
        ref,
        index,
        reorder,
        type,
    });
    drop(ref);

    const startEditing = useCallback(() => {
        setIsEditing(true);
    }, []);

    const cancelEditing = useCallback(() => {
        setIsEditing(false);
    }, []);

    const save = useCallback(
        async (
            values: StageNameFormValues,
            formik: FormikHelpers<StageNameFormValues>,
        ) => {
            const trimmed = values.name.trim();
            if (!trimmed) {
                return;
            }
            await renameMutation.mutateAsync({ stageId: stage.id, name: trimmed });
            handleEvent("referral-pipeline-stage-rename");
            formik.resetForm();
            setIsEditing(false);
            onChanged();
        },
        [renameMutation, stage.id, onChanged],
    );

    const handleDelete = useCallback(async () => {
        try {
            await deleteMutation.mutateAsync({ stageId: stage.id });
            handleEvent("referral-pipeline-stage-delete");
            onChanged();
        } catch (error) {
            await fancyAlert(
                "Cannot delete stage",
                error instanceof Error ? error.message : "Unable to delete this stage",
            );
        }
    }, [deleteMutation, stage.id, onChanged]);

    return (
        <li className="py-2" ref={ref}>
            <div
                ref={preview}
                className={`flex items-center space-x-2 ${
                    isDragging ? "opacity-0" : ""
                }`}>
                <div className="cursor-move p-1" ref={drag}>
                    <Bars3Icon className="w-5 h-5 text-gray-500" />
                </div>
                {isEditing ? (
                    <div className="flex-1">
                        <Formik initialValues={{ name: stage.name }} onSubmit={save}>
                            {formValues => (
                                <form onSubmit={formValues.handleSubmit}>
                                    <div className="flex items-center space-x-1">
                                        <Input
                                            type="text"
                                            name="name"
                                            autoFocus
                                            textSize="sm"
                                            onChange={formValues.handleChange}
                                            onBlur={formValues.handleBlur}
                                            value={formValues.values.name}
                                        />
                                        <PrimaryButton
                                            disabled={
                                                formValues.isSubmitting ||
                                                formValues.values.name.trim().length === 0
                                            }
                                            type="submit">
                                            Save
                                        </PrimaryButton>
                                        <SecondaryButton
                                            type="button"
                                            onClick={cancelEditing}>
                                            Cancel
                                        </SecondaryButton>
                                    </div>
                                </form>
                            )}
                        </Formik>
                    </div>
                ) : (
                    <>
                        <p className="flex-1 truncate text-sm text-gray-900">
                            {stage.name}
                        </p>
                        <EditButton
                            backgroundColor="bg-white"
                            size="sm"
                            onClick={startEditing}
                        />
                        <DeleteButton
                            backgroundColor="bg-white"
                            size="sm"
                            confirm={{
                                title: "Delete stage",
                                message: `Are you sure you want to delete the "${stage.name}" stage?`,
                            }}
                            onClick={handleDelete}
                        />
                    </>
                )}
            </div>
        </li>
    );
};

interface ReferralPipelineStageRowNewProps {
    pipelineId: PipelineId;
    close: () => void;
    onChanged: () => void;
}

const ReferralPipelineStageRowNew: React.FC<ReferralPipelineStageRowNewProps> = props => {
    const { pipelineId, close, onChanged } = props;
    const trpcClient = trpc.useTRPC();
    const createStageMutation = useMutation({
        ...trpcClient.pipeline.createStage.mutationOptions(),
    });
    const inputRef = React.useRef<HTMLInputElement>(null);

    const save = useCallback(
        async (
            values: StageNameFormValues,
            formik: FormikHelpers<StageNameFormValues>,
        ) => {
            const trimmed = values.name.trim();
            if (!trimmed) {
                return;
            }
            await createStageMutation.mutateAsync({ pipelineId, name: trimmed });
            handleEvent("referral-pipeline-stage-create");
            formik.resetForm();
            onChanged();
            inputRef.current?.focus();
        },
        [createStageMutation, pipelineId, onChanged],
    );

    return (
        <Formik initialValues={{ name: "" }} onSubmit={save}>
            {formValues => (
                <form onSubmit={formValues.handleSubmit}>
                    <div className="flex items-end space-x-1">
                        <Input
                            ref={inputRef}
                            type="text"
                            label="New stage"
                            name="name"
                            autoFocus
                            textSize="sm"
                            onChange={formValues.handleChange}
                            onBlur={formValues.handleBlur}
                            value={formValues.values.name}
                        />
                        <PrimaryButton
                            disabled={
                                formValues.isSubmitting ||
                                formValues.values.name.trim().length === 0
                            }
                            type="submit">
                            Create
                        </PrimaryButton>
                        <SecondaryButton type="button" onClick={close}>
                            Cancel
                        </SecondaryButton>
                    </div>
                </form>
            )}
        </Formik>
    );
};
